import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PreviewLink } from './PreviewLink';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { getInitials } from '../lib/people';
import { apiPatch } from '../lib/api';

export interface FollowUpTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  created_at: string;
  person_id: string;
  person_name: string;
  source_meeting_id?: string | null;
  meeting_date?: string | null;
}

interface FollowUpListProps {
  followUps: FollowUpTask[];
  showMeetingDate?: boolean;
  onStatusToggle?: () => void;
  meetingName?: string;
  instanceId?: string;
}

export function FollowUpList({ followUps, showMeetingDate, onStatusToggle, meetingName, instanceId }: FollowUpListProps) {
  // The real meeting URL: /meetings/<id> alone is the SERIES route (#267).
  const { pathname } = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  if (followUps.length === 0) {
    return (
      <p className="text-xs text-text-muted py-1">No follow-ups yet</p>
    );
  }

  // Group by person
  const grouped = new Map<string, { personName: string; tasks: FollowUpTask[] }>();
  for (const fu of followUps) {
    if (!grouped.has(fu.person_id)) {
      grouped.set(fu.person_id, { personName: fu.person_name, tasks: [] });
    }
    grouped.get(fu.person_id)!.tasks.push(fu);
  }

  function toggleGroup(personId: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(personId)) {
        next.delete(personId);
      } else {
        next.add(personId);
      }
      return next;
    });
  }

  async function toggleTaskStatus(task: FollowUpTask) {
    const newStatus = task.status === 'done' ? 'todo' : 'done';
    try {
      await apiPatch(`/tasks/${task.id}`, { status: newStatus });
      task.status = newStatus;
      onStatusToggle?.();
    } catch (err) {
      console.error('Failed to toggle task status:', err);
    }
  }

  return (
    <div className="space-y-2">
      {Array.from(grouped.entries()).map(([personId, { personName, tasks }]) => {
        const isCollapsed = collapsedGroups.has(personId);
        return (
          <div key={personId}>
            <button
              onClick={() => toggleGroup(personId)}
              className="flex items-center gap-2 w-full text-left py-1 hover:bg-bg-hover rounded px-1 transition-colors"
            >
              {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
              <div className="w-5 h-5 rounded-full bg-accent/20 text-accent flex items-center justify-center text-[9px] font-semibold flex-shrink-0">
                {getInitials(personName)}
              </div>
              <span className="text-xs font-medium text-text-primary truncate flex-1">{personName}</span>
              <span className="text-[10px] text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">{tasks.length}</span>
            </button>
            {!isCollapsed && (
              <div className="ml-5 pl-2 border-l border-border space-y-0.5 mt-0.5">
                {tasks.map(task => (
                  <div key={task.id} className="flex items-start gap-1.5 py-0.5 group">
                    <button
                      onClick={() => toggleTaskStatus(task)}
                      className={`mt-0.5 w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                        task.status === 'done'
                          ? 'bg-success border-success'
                          : 'border-border-hover bg-bg-input hover:border-accent'
                      }`}
                    >
                      {task.status === 'done' && (
                        <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                          <path d="M3 6l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                    <div className="min-w-0 flex-1">
                      <PreviewLink
                        to={`/tasks/${task.id}`}
                        type="task"
                        id={task.id}
                        state={meetingName && instanceId ? { from: pathname, fromLabel: meetingName } : undefined}
                        className={`text-xs hover:text-accent transition-colors block truncate ${
                          task.status === 'done' ? 'text-text-muted line-through' : 'text-text-primary'
                        }`}
                      >
                        {task.title}
                      </PreviewLink>
                      {showMeetingDate && task.meeting_date && (
                        <span className="text-[10px] text-text-muted">
                          {new Date(task.meeting_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
